import styled, { css, keyframes } from "styled-components";
import { deviceMax, reducedMotion } from "../../../utils/device";

const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

export const Frame = styled.div`
  margin: 0 auto;
  max-width: 330px;
  position: relative;
  width: 100%;
`;

export const Card = styled.div(
  ({ theme }) => css`
    aspect-ratio: 330 / 470;
    background: ${theme.colors.white};
    border: 1px solid ${theme.colors.brand.line};
    border-radius: ${theme.layout.radius.panel};
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.13);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
    width: 100%;

    @media ${deviceMax.phoneMax} {
      border-radius: ${theme.layout.radius.card};
    }
  `
);

export const StaticCard = styled(Card)`
  aspect-ratio: auto;
`;

export const CardHeader = styled.header(
  ({ theme }) => css`
    align-items: center;
    background: ${theme.colors.brand.deep};
    display: flex;
    flex: none;
    height: 38px;
    justify-content: center;
    position: relative;

    &::before {
      background: ${theme.gradients.brand};
      content: "";
      height: 3px;
      left: 0;
      position: absolute;
      right: 0;
      top: 0;
    }
  `
);

export const HeaderMark = styled.img`
  height: 18px;
  width: auto;
`;

export const CardBody = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
`;

export const StaticBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
`;

export const ScrollContent = styled.div<{ $stage: number; $leaving: boolean }>(
  ({ $stage, $leaving }) => css`
    display: flex;
    flex-direction: column;
    height: 100%;
    inset: 0;
    opacity: ${$leaving ? 0 : 1};
    position: absolute;
    transition: transform 0.85s ${EASE}, opacity 0.65s ease;
    transform: translateY(calc(-${$stage * 100}% + ${$leaving ? 30 : 0}px));

    @media ${reducedMotion} {
      transition: none;
    }
  `
);

export const Page = styled.div`
  display: flex;
  flex: 0 0 100%;
  flex-direction: column;
  gap: 14px;
  height: 100%;
  justify-content: center;
  padding: 16px;
`;

export const EventTitle = styled.p(
  ({ theme }) => css`
    color: ${theme.colors.brand.muted};
    font-family: ${theme.typography.fonts.body};
    font-size: 11px;
    font-weight: ${theme.typography.weights.bold};
    letter-spacing: 0.02em;
    margin: 0;
  `
);

export const SubCard = styled.div(
  ({ theme }) => css`
    background: ${theme.colors.white};
    border: 1px solid ${theme.colors.brand.line};
    border-radius: ${theme.layout.radius.card};
    padding: 14px;
    position: relative;
  `
);

export const QuestionText = styled.p(
  ({ theme }) => css`
    color: ${theme.colors.brand.ink};
    font-family: ${theme.typography.fonts.body};
    font-size: 13px;
    font-weight: 650;
    line-height: 1.4;
    margin: 0 0 12px;
  `
);

export const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const PollOption = styled.div<{ $selected: boolean }>(
  ({ theme, $selected }) => css`
    align-items: center;
    background: ${$selected ? theme.colors.brand.soft : theme.colors.white};
    border: 1px solid
      ${$selected ? theme.colors.brand.action : theme.colors.brand.line};
    border-radius: ${theme.layout.radius.button};
    color: ${theme.colors.brand.ink};
    display: flex;
    font-size: 12px;
    padding: 9px 11px;
    position: relative;
    transition: background 0.3s ease, border-color 0.3s ease;
  `
);

export const SaveTimerTrack = styled.div(
  ({ theme }) => css`
    background: ${theme.colors.brand.line};
    border-radius: 999px;
    height: 3px;
    margin-top: 12px;
    overflow: hidden;
    width: 100%;
  `
);

export const SaveTimerFill = styled.div<{ $active: boolean }>(
  ({ theme, $active }) => css`
    background: ${theme.gradients.brand};
    height: 100%;
    transform: scaleX(${$active ? 1 : 0});
    transform-origin: left;
    transition: transform 0.9s ease;
    width: 100%;

    @media ${reducedMotion} {
      transition: none;
    }
  `
);

export const VoteRow = styled.div`
  align-items: center;
  display: flex;
  gap: 6px;
  justify-content: flex-end;
  margin-top: 10px;
`;

export const VoteCheck = styled.span<{ $saved: boolean }>(
  ({ theme, $saved }) => css`
    align-items: center;
    color: ${$saved ? theme.colors.brand.deep : theme.colors.brand.line};
    display: flex;
    transition: color 0.3s ease;

    svg {
      height: 13px;
      width: 13px;
    }
  `
);

export const VoteCount = styled.span(
  ({ theme }) => css`
    color: ${theme.colors.brand.muted};
    font-size: 11px;
    font-variant-numeric: tabular-nums;
  `
);

export const NpsTrack = styled.div(
  ({ theme }) => css`
    background: ${theme.gradients.brand};
    border-radius: 999px;
    height: 5px;
    margin: 20px 4px 0;
    position: relative;
  `
);

export const NpsThumb = styled.div<{ $pct: number }>(
  ({ theme, $pct }) => css`
    background: ${theme.colors.white};
    border: 3px solid ${theme.colors.brand.action};
    border-radius: 50%;
    box-shadow: 0 2px 6px rgba(16, 32, 48, 0.25);
    height: 18px;
    left: ${$pct}%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    transition: left 0.9s ${EASE};
    width: 18px;

    @media ${reducedMotion} {
      transition: none;
    }
  `
);

export const NpsTicks = styled.div(
  ({ theme }) => css`
    color: ${theme.colors.brand.muted};
    display: flex;
    font-size: 9px;
    font-variant-numeric: tabular-nums;
    justify-content: space-between;
    margin-top: 7px;
    padding: 0 1px;
  `
);

export const NpsScaleRow = styled.div(
  ({ theme }) => css`
    color: ${theme.colors.brand.muted};
    display: flex;
    font-size: 10px;
    justify-content: space-between;
    margin-top: 4px;
  `
);

export const TypedTextBox = styled.div(
  ({ theme }) => css`
    background: ${theme.colors.brand.soft};
    border: 1px solid ${theme.colors.brand.line};
    border-radius: ${theme.layout.radius.button};
    color: ${theme.colors.brand.ink};
    font-size: 12px;
    line-height: 1.5;
    min-height: 44px;
    padding: 10px 12px;
  `
);

const blink = keyframes`
  0%, 55% { opacity: 1; }
  56%, 100% { opacity: 0; }
`;

export const Caret = styled.span<{ $blink: boolean }>(
  ({ theme, $blink: shouldBlink }) => css`
    animation: ${shouldBlink ? blink : "none"} 1s step-end infinite;
    background: ${theme.colors.brand.navy};
    display: inline-block;
    height: 12px;
    margin-left: 1px;
    vertical-align: -2px;
    width: 1.5px;

    @media ${reducedMotion} {
      animation: none;
    }
  `
);

export const SubmitRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
`;

export const SubmitButton = styled.div(
  ({ theme }) => css`
    background: ${theme.colors.brand.line};
    border-radius: ${theme.layout.radius.button};
    color: ${theme.colors.brand.muted};
    font-size: 11px;
    font-weight: ${theme.typography.weights.bold};
    letter-spacing: 0.03em;
    padding: 8px 16px;
    text-transform: uppercase;
  `
);

export const Cursor = styled.div<{ $visible: boolean }>(
  ({ theme, $visible }) => css`
    background: ${theme.colors.white};
    border: 2px solid ${theme.colors.brand.action};
    border-radius: 50%;
    box-shadow: 0 3px 10px rgba(16, 32, 48, 0.3);
    height: 20px;
    opacity: ${$visible ? 1 : 0};
    pointer-events: none;
    position: absolute;
    transition: opacity 0.25s ease, transform 0.25s ease;
    transform: scale(${$visible ? 1 : 0.5});
    width: 20px;
    z-index: 2;

    @media ${reducedMotion} {
      display: none;
    }
  `
);

export const Caption = styled.p<{ $visible: boolean }>(
  ({ theme, $visible }) => css`
    color: ${theme.colors.brand.muted};
    font-size: 12px;
    margin: 14px 0 0;
    opacity: ${$visible ? 1 : 0};
    text-align: center;
    transition: opacity 0.5s ease;

    @media ${reducedMotion} {
      transition: none;
    }
  `
);
